import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { identifyPatient, resetIdentity } from '@/lib/analytics';
import { cart } from '@/features/marketplace/stores/cart';
import { startCartContextSync } from '@/lib/api/cartContext';
import { clearMembershipLinks } from '@/features/membership/membershipLinks';

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const publishedUserId = useRef<string | null | undefined>(undefined);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let authEventReceived = false;
    const publishSession = (nextSession: Session | null, signedOut = false) => {
      const nextUserId = nextSession?.user.id ?? null;
      // Shared portal hooks use account-independent keys. Clear all cached
      // patient/admin/profile/card data before consumers see another identity.
      // A refresh for the same user keeps the cache and in-flight work intact.
      if (signedOut || publishedUserId.current !== nextUserId) {
        const queue = { queryKey: ['admin', 'support-review'] };
        void queryClient.cancelQueries(queue);
        queryClient.removeQueries(queue);
        queryClient.clear();
      }
      publishedUserId.current = nextUserId;
      setSession(nextSession);
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted || authEventReceived) return;
      if (data.session?.user.id) cart.setOwner(data.session.user.id);
      publishSession(data.session);
      if (data.session?.user) {
        identifyPatient({
          id: data.session.user.id,
          email: data.session.user.email,
        });
      }
    }).catch(() => {
      // A failed/expired sign-in callback must not strand the patient on
      // Loading forever. The protected-route guard handles fresh sign-in.
      if (mounted) setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, s) => {
      if (!mounted) return;
      authEventReceived = true;
      if (s?.user.id) cart.setOwner(s.user.id);
      publishSession(s, event === 'SIGNED_OUT');
      if (event === 'SIGNED_OUT') {
        // Real logout on a possibly-shared device: drop the identity.
        resetIdentity();
        cart.clear();
        clearMembershipLinks();
      } else if (s?.user) {
        identifyPatient({ id: s.user.id, email: s.user.email });
      }
      // No-session INITIAL_SESSION must NOT reset: it fires on every fresh
      // page load (including the post-checkout claim landing), and reset()
      // discards the marketing-site anonymous distinct_id that the
      // .systemlabs.com PostHog cookie just carried over — then writes the
      // fresh id back over the shared cookie. That orphaned every
      // marketing→portal identity merge (persons "first seen" at the
      // portal, cross-site funnels empty) from May 22 until 2026-07-07.
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [queryClient]);

  useEffect(() => startCartContextSync(session), [session]);

  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        signOut: async () => {
          await supabase.auth.signOut();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
